import { useEffect, useState, useContext } from 'react'
import styled from 'styled-components'
import { IntakeContext } from '../context/IntakeContext'
// import { Link } from "react-router-dom";
import { selectData } from 'layouts/onboarding'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'
import { useFieldFocus } from 'context/FieldFocusContext'

// @mui material components
import HelpIcon from '@mui/icons-material/Help'
import { IconButton, Tooltip, Fade, Grid, Typography, Box } from '@mui/material'

// images
import fers from 'assets/images/fers.png'
import csrs from 'assets/images/csrs.png'

const StyledMSB = styled.div`
  text-align: center;
  position: relative;
  padding: 16px;
  height: 100%;
  cursor: pointer;
  border: ${(props) =>
    props.active ? '2px solid #a3a7aa' : '2px solid #e5e5e5'};
  background: ${(props) => (props.active ? '#eeeeee' : '#fff')};
  color: #212121;
  border-radius: 0.75rem;
  transition: 0.2s all ease-in-out;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  &:hover {
    border: 2px solid #a3a7aa;
    background: #f5f5f5;
  }

  > h6,
  h5 {
    color: #212121;
    letter-spacing: 0.3px;
    font-weight: 600;
    line-height: 1.4;
    margin: 0;
  }

  > span {
    border-radius: 50%;
    height: 1.5em;
    width: 1.5em;
    padding: 8px;
    display: flex;
    justify-content: center;
    align-items: center;

    &.yesIcon {
      color: #4caf50;
      background-color: #dbefdc;
    }
    &.noIcon {
      color: #fd5c70;
      background-color: #fedee2;
    }
  }
`
const StyledAgencyLogo = styled.img`
  max-height: 60px;
  width: auto;
  max-width: 100%;
`

const RetirementSystemTileSelector = () => {
  const { intakeState, updateIntakeStateAtKey } = useContext(IntakeContext)

  const [retirementSystem, setRetirementSystem] = useState(
    intakeState.retirementSystem
  )

  const toggleRetirementSystem = (name) => {
    setRetirementSystem(name)
  }

  useEffect(() => {
    if (typeof retirementSystem != 'string') return
    updateIntakeStateAtKey({
      retirementSystem: retirementSystem,
      estimatedSocialSecurity: retirementSystem === 'csrs' ? 0 : 2400,
    })
  }, [retirementSystem])

  const [isMobile, setIsMobile] = useState(window.innerWidth < 992)
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const fieldId = 'rsHelp'
  const helpText =
    'Retirement systems have specific nuances to retirement rules. This will change our calculations and recommendations for you.'

  const { handleFocus, handleBlur, registerHelpField } = useFieldFocus()
  useEffect(() => {
    registerHelpField(fieldId, helpText)
  }, [fieldId])

  return (
    <Box
      sx={{ textAlign: 'left' }}
      onMouseEnter={(e) => handleFocus(fieldId, e.currentTarget)}
      onMouseLeave={() => handleBlur()}
    >
      <Box mb={1}>
        <Typography variant="h4" fontSize={{ xs: '20px', sm: '24px' }}>
          Which retirement system are you a part of?
          {isMobile && (
            <Tooltip
              TransitionComponent={Fade}
              title="Retirement systems have specific nuances to retirement rules. This will change our calculations and recommendations for you."
              placement="top"
              color="info"
              enterTouchDelay={0}
              leaveTouchDelay={3000}
            >
              <IconButton size="small" color="dark">
                <HelpIcon sx={{ marginTop: '-10px' }} />
              </IconButton>
            </Tooltip>
          )}
        </Typography>
      </Box>
      <Typography variant="body1" fontSize={{ xs: '16px', sm: '20px' }}>
        Select one of the following:
      </Typography>
      <Box my={3}>
        <Grid container spacing={1} justifyContent="center" mb={3}>
          <Grid item xs={12}>
            {renderMuiFormField({
              autoFocus: false,
              id: 'retirementSystem',
              label: '🔎︎ Find Your Retirement System',
              hiddenLabel: true,
              inputType: 'select',
              value: intakeState.retirementSystem,
              searchable: true,
              enableHelpText: true,
              helpText:
                'Retirement systems have specific nuances to retirement rules. This will change our calculations and recommendations for you.',
              onChange: (retirementSystem) =>
                updateIntakeStateAtKey({ retirementSystem: retirementSystem }),
              options: selectData.retirementSystem,
            })}
          </Grid>
          <Grid item xs={6}>
            <StyledMSB
              active={retirementSystem === 'fers'}
              onClick={() => toggleRetirementSystem('fers')}
            >
              <StyledAgencyLogo src={fers} />
              <Typography variant="h6" textTransform="capitalize">
                FERS
              </Typography>
            </StyledMSB>
          </Grid>
          <Grid item xs={6}>
            <StyledMSB
              active={retirementSystem === 'special_fers_(leo,_atc_etc.)'}
              onClick={() =>
                toggleRetirementSystem('special_fers_(leo,_atc_etc.)')
              }
            >
              <StyledAgencyLogo src={fers} />
              <Typography variant="h6" textTransform="capitalize">
                Special FERS (LEO, ATC etc.)
                {/* Special FERS (Firefighter) */}
              </Typography>
            </StyledMSB>
          </Grid>
          <Grid item xs={6}>
            <StyledMSB
              active={retirementSystem === 'csrs'}
              onClick={() => toggleRetirementSystem('csrs')}
            >
              <StyledAgencyLogo src={csrs} />
              <Typography variant="h6" textTransform="capitalize">
                CSRS
              </Typography>
            </StyledMSB>
          </Grid>
          <Grid item xs={6}>
            <StyledMSB
              active={
                retirementSystem === 'foreign_service_officer_pension_(fsps)'
              }
              onClick={() =>
                toggleRetirementSystem('foreign_service_officer_pension_(fsps)')
              }
            >
              <StyledAgencyLogo src="https://www.state.gov/wp-content/uploads/2022/04/cropped-dos_seal-192x192.png" />
              <Typography variant="h6" textTransform="capitalize">
                FSPS
              </Typography>
            </StyledMSB>
          </Grid>
        </Grid>
        {/* <Grid container>
          {!excludedSystems.includes(retirementSystem) ? (
            <>
              <Grid item xs={12} mb={2}>
                {renderMuiFormField({
                  autoFocus: false,
                  id: 'retirementSystem',
                  label: 'Retirement System',
                  placeholder: '',
                  inputType: 'select',
                  value: intakeState.retirementSystem,
                  onChange: (retirementSystem) =>
                    updateIntakeStateAtKey({ retirementSystem }),
                  options: selectData.retirementSystem,
                })}
              </Grid>
            </>
          ) : (
            <></>
          )}
        </Grid> */}
      </Box>
    </Box>
  )
}

export default RetirementSystemTileSelector
